import {
    CalendarDate,
    formatDateNoTime,
    FranchiseFeeStructureId,
    FranchiseFeeStructureMode,
    FranchiseTerritoryId,
    FranchiseTerritoryStatus,
    RegionId,
    rootRegionParentId,
} from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { DeleteButton } from "components/ui/action-button";
import { Badge } from "components/ui/badge";
import { CalendarDateInput } from "components/ui/calendar-date";
import { DatePicker } from "components/ui/date-time-picker/date-picker";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { Panel } from "components/ui/panel/panel";
import { PanelType } from "components/ui/panel/panel-type";
import { PrimaryButton } from "components/ui/primary-button";
import { ReorderableStringList } from "components/ui/reorderable-string-list";
import { SecondaryButton, SecondaryCompactButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { useMessage } from "hooks/use-message";
import React from "react";
import { useSelector } from "react-redux";
import { ApplicationState } from "store/state";
import {
    computeEffectiveDate,
    feeAmountColumnLabel,
    feeScheduleLabel,
    feeStructureModeLabel,
    formatDollars,
    modeChangePatch,
    nextFormKey,
    parseBasisPoints,
    parseCents,
    stepWhenLabel,
} from "./fee-schedule-form";
import { TerritoryRow } from "./types";

interface TerritoryFormData {
    name: string;
    code: string;
    status: FranchiseTerritoryStatus;
    zipCodes: string[];
    assignedRegionId: RegionId | "";
    purchaseDate: CalendarDate | null;
    trainingDate: CalendarDate | null;
}

export interface ScheduleEntryFormData {
    _key: string;
    effectiveDate: string;
    amountDollars: string;
}

export interface ScheduleStepFormData {
    _key: string;
    monthsAfterCommencement: number;
    amountDollars: string;
}

export type FeeStructureScheduleFormData =
    | { kind: "relative"; steps: ScheduleStepFormData[] }
    | { kind: "absolute"; entries: ScheduleEntryFormData[] };

export interface FeeStructureFormData {
    _key: string;
    existingId: FranchiseFeeStructureId | null;
    name: string;
    mode: FranchiseFeeStructureMode;
    feePercentDisplay: string;
    commencementDate: CalendarDate | null;
    fromDefault: boolean;
    schedule: FeeStructureScheduleFormData;
}

interface TerritoryFormPanelProps {
    onDismiss: () => void;
    onSuccess: () => void;
    editingTerritory?: TerritoryRow;
    initialFeeStructures: FeeStructureFormData[];
}

function sortedSteps(steps: ScheduleStepFormData[]): ScheduleStepFormData[] {
    return [...steps].sort(
        (a, b) => a.monthsAfterCommencement - b.monthsAfterCommencement,
    );
}

function buildScheduleEntries(
    fs: FeeStructureFormData,
): { effectiveDate: string; amountCents: number }[] {
    if (fs.schedule.kind === "absolute") {
        return fs.schedule.entries.map(e => ({
            effectiveDate: e.effectiveDate,
            amountCents: parseCents(e.amountDollars),
        }));
    }
    const commencementDate = fs.commencementDate;
    if (!commencementDate) {
        throw new Error(
            `Set the commencement date for "${fs.name}", or edit its schedule manually.`,
        );
    }
    return sortedSteps(fs.schedule.steps).map(step => ({
        effectiveDate: commencementDate
            .addMonths(step.monthsAfterCommencement)
            .toString(),
        amountCents: parseCents(step.amountDollars),
    }));
}

export const TerritoryFormPanel: React.FC<TerritoryFormPanelProps> = props => {
    const { onDismiss, onSuccess, editingTerritory, initialFeeStructures } = props;

    const isEditing = !!editingTerritory;
    const [formData, setFormData] = React.useState<TerritoryFormData>(() =>
        editingTerritory
            ? {
                  name: editingTerritory.name,
                  code: editingTerritory.code,
                  status: editingTerritory.status,
                  zipCodes: editingTerritory.zipCodes,
                  assignedRegionId: editingTerritory.assignedRegionId ?? "",
                  purchaseDate: editingTerritory.purchaseDate
                      ? CalendarDate.parse(editingTerritory.purchaseDate).getOrElse(null)
                      : null,
                  trainingDate: editingTerritory.trainingDate
                      ? CalendarDate.parse(editingTerritory.trainingDate).getOrElse(null)
                      : null,
              }
            : {
                  name: "",
                  code: "",
                  status: FranchiseTerritoryStatus.Active,
                  zipCodes: [],
                  assignedRegionId: "",
                  purchaseDate: null,
                  trainingDate: null,
              },
    );
    const [feeStructures, setFeeStructures] =
        React.useState<FeeStructureFormData[]>(initialFeeStructures);
    const [originalFeeStructureIds, setOriginalFeeStructureIds] = React.useState(() =>
        initialFeeStructures.filter(fs => fs.existingId).map(fs => fs.existingId!),
    );
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const [isSaving, setIsSaving] = React.useState(false);
    const createdTerritoryIdRef = React.useRef<FranchiseTerritoryId | null>(null);
    const persistedFeeStructureKeysRef = React.useRef(new Set<string>());
    const { message: panelMessage, setMessage: setPanelMessage } = useMessage();

    const regions = useSelector((state: ApplicationState) => state.region.regions);
    const childRegions = regions.filter(r => r.parentRegionId !== rootRegionParentId);

    const trpcClient = trpc.useTRPC();

    const createMutation = useMutation({
        ...trpcClient.franchiseTerritory.create.mutationOptions(),
    });

    const updateMutation = useMutation({
        ...trpcClient.franchiseTerritory.update.mutationOptions(),
    });

    const createFeeStructureMutation = useMutation({
        ...trpcClient.franchiseTerritory.createFeeStructure.mutationOptions(),
    });

    const updateFeeStructureMutation = useMutation({
        ...trpcClient.franchiseTerritory.updateFeeStructure.mutationOptions(),
    });

    const deleteFeeStructureMutation = useMutation({
        ...trpcClient.franchiseTerritory.deleteFeeStructure.mutationOptions(),
    });

    const updateFeeStructureField = React.useCallback(
        <K extends keyof FeeStructureFormData>(
            fsIndex: number,
            field: K,
            value: FeeStructureFormData[K],
        ) => {
            setFeeStructures(prev => {
                const updated = [...prev];
                updated[fsIndex] = { ...updated[fsIndex], [field]: value };
                return updated;
            });
        },
        [],
    );

    const updateScheduleEntry = React.useCallback(
        (
            fsIndex: number,
            entryIndex: number,
            field: keyof ScheduleEntryFormData,
            value: string,
        ) => {
            setFeeStructures(prev => {
                const updated = [...prev];
                const schedule = updated[fsIndex].schedule;
                if (schedule.kind !== "absolute") {
                    return prev;
                }
                const entries = [...schedule.entries];
                entries[entryIndex] = {
                    ...entries[entryIndex],
                    [field]: value,
                };
                updated[fsIndex] = {
                    ...updated[fsIndex],
                    schedule: { kind: "absolute", entries },
                };
                return updated;
            });
        },
        [],
    );

    const addFeeStructure = React.useCallback(() => {
        setFeeStructures(prev => [
            ...prev,
            {
                _key: nextFormKey(),
                existingId: null,
                name: "",
                mode: FranchiseFeeStructureMode.PercentPlusMinimum,
                feePercentDisplay: "",
                commencementDate: null,
                fromDefault: false,
                schedule: {
                    kind: "absolute",
                    entries: [
                        {
                            _key: nextFormKey(),
                            effectiveDate: "",
                            amountDollars: "",
                        },
                    ],
                },
            },
        ]);
    }, []);

    const removeFeeStructure = React.useCallback((fsIndex: number) => {
        setFeeStructures(prev => prev.filter((_, i) => i !== fsIndex));
    }, []);

    const addScheduleEntry = React.useCallback((fsIndex: number) => {
        setFeeStructures(prev => {
            const updated = [...prev];
            const schedule = updated[fsIndex].schedule;
            if (schedule.kind !== "absolute") {
                return prev;
            }
            updated[fsIndex] = {
                ...updated[fsIndex],
                schedule: {
                    kind: "absolute",
                    entries: [
                        ...schedule.entries,
                        {
                            _key: nextFormKey(),
                            effectiveDate: "",
                            amountDollars: "",
                        },
                    ],
                },
            };
            return updated;
        });
    }, []);

    const removeScheduleEntry = React.useCallback(
        (fsIndex: number, entryIndex: number) => {
            setFeeStructures(prev => {
                const updated = [...prev];
                const schedule = updated[fsIndex].schedule;
                if (schedule.kind !== "absolute") {
                    return prev;
                }
                updated[fsIndex] = {
                    ...updated[fsIndex],
                    schedule: {
                        kind: "absolute",
                        entries: schedule.entries.filter((_, i) => i !== entryIndex),
                    },
                };
                return updated;
            });
        },
        [],
    );

    const editScheduleManually = React.useCallback((fsIndex: number) => {
        setFeeStructures(prev => {
            const updated = [...prev];
            const fs = updated[fsIndex];
            if (fs.schedule.kind !== "relative") {
                return prev;
            }
            const entries = sortedSteps(fs.schedule.steps).map(step => {
                const effective = computeEffectiveDate(
                    fs.commencementDate,
                    step.monthsAfterCommencement,
                );
                return {
                    _key: nextFormKey(),
                    effectiveDate: effective ? effective.toString() : "",
                    amountDollars: step.amountDollars,
                };
            });
            updated[fsIndex] = {
                ...fs,
                fromDefault: false,
                schedule: { kind: "absolute", entries },
            };
            return updated;
        });
    }, []);

    const resetToDefaults = React.useCallback(async () => {
        const confirmed = await fancyConfirm(
            "Reset fee structures?",
            "Replace all fee structures below with your territory defaults?",
            "Reset",
            "Cancel",
        );
        if (!confirmed) {
            return;
        }
        setFeeStructures(initialFeeStructures);
    }, [initialFeeStructures]);

    const changeMode = React.useCallback(
        (fsIndex: number, newMode: FranchiseFeeStructureMode) => {
            setFeeStructures(prev =>
                prev.map((fs, i) =>
                    i === fsIndex
                        ? { ...fs, ...modeChangePatch(fs.feePercentDisplay, newMode) }
                        : fs,
                ),
            );
        },
        [],
    );

    const validateFeeStructures = React.useCallback((): string | null => {
        for (let i = 0; i < feeStructures.length; i++) {
            const fs = feeStructures[i];
            if (!fs.name.trim()) {
                return `Fee structure #${i + 1}: Name is required.`;
            }
            if (
                fs.mode === FranchiseFeeStructureMode.PercentPlusMinimum &&
                (!fs.feePercentDisplay.trim() || parseFloat(fs.feePercentDisplay) <= 0)
            ) {
                return `Fee structure "${fs.name || i + 1}": Fee percent is required and must be greater than 0.`;
            }
            if (fs.schedule.kind === "relative") {
                if (!fs.commencementDate) {
                    return `Set the commencement date for "${fs.name}", or edit its schedule manually.`;
                }
                continue;
            }
            if (fs.schedule.entries.length === 0) {
                return `Fee structure "${fs.name || i + 1}": At least one fee schedule entry is required.`;
            }
            for (let j = 0; j < fs.schedule.entries.length; j++) {
                const entry = fs.schedule.entries[j];
                if (!entry.effectiveDate) {
                    return `Fee structure "${fs.name || i + 1}", entry #${j + 1}: Effective date is required.`;
                }
                if (!entry.amountDollars.trim() || parseFloat(entry.amountDollars) < 0) {
                    return `Fee structure "${fs.name || i + 1}", entry #${j + 1}: Amount is required.`;
                }
            }
        }
        return null;
    }, [feeStructures]);

    const buildFeeStructureInput = React.useCallback((fs: FeeStructureFormData) => {
        const scheduleEntries = buildScheduleEntries(fs);
        return {
            name: fs.name,
            mode: fs.mode,
            ...(fs.mode === FranchiseFeeStructureMode.PercentPlusMinimum
                ? {
                      feePercentBasisPoints: parseBasisPoints(fs.feePercentDisplay),
                  }
                : {}),
            ...(fs.commencementDate
                ? { commencementDate: fs.commencementDate.toString() }
                : {}),
            scheduleEntries,
        };
    }, []);

    const handleSubmit = React.useCallback(async () => {
        if (!formData.name.trim()) {
            setValidationError("Territory name is required.");
            return;
        }
        if (!formData.code.trim()) {
            setValidationError("Territory code is required.");
            return;
        }
        if (formData.zipCodes.length === 0) {
            setValidationError("At least one zip code is required.");
            return;
        }

        const fsError = validateFeeStructures();
        if (fsError) {
            setValidationError(fsError);
            return;
        }

        setValidationError(null);
        setIsSaving(true);

        try {
            if (isEditing && editingTerritory) {
                await updateMutation.mutateAsync({
                    id: editingTerritory.id,
                    name: formData.name,
                    code: formData.code,
                    status: formData.status,
                    zipCodes: formData.zipCodes,
                    assignedRegionId: formData.assignedRegionId || null,
                    purchaseDate: formData.purchaseDate?.toString() ?? null,
                    trainingDate: formData.trainingDate?.toString() ?? null,
                });

                const currentExistingIds = new Set(
                    feeStructures.filter(fs => fs.existingId).map(fs => fs.existingId!),
                );

                for (const id of originalFeeStructureIds) {
                    if (!currentExistingIds.has(id)) {
                        await deleteFeeStructureMutation.mutateAsync({
                            feeStructureId: id,
                        });
                        setOriginalFeeStructureIds(prev =>
                            prev.filter(originalId => originalId !== id),
                        );
                    }
                }

                for (const fs of feeStructures.filter(f => !f.existingId)) {
                    if (persistedFeeStructureKeysRef.current.has(fs._key)) {
                        continue;
                    }
                    await createFeeStructureMutation.mutateAsync({
                        territoryId: editingTerritory.id,
                        ...buildFeeStructureInput(fs),
                    });
                    persistedFeeStructureKeysRef.current.add(fs._key);
                }

                for (const fs of feeStructures.filter(f => f.existingId)) {
                    await updateFeeStructureMutation.mutateAsync({
                        feeStructureId: fs.existingId!,
                        ...buildFeeStructureInput(fs),
                    });
                }
            } else {
                if (!createdTerritoryIdRef.current) {
                    const created = await createMutation.mutateAsync({
                        name: formData.name,
                        code: formData.code,
                        status: formData.status,
                        zipCodes: formData.zipCodes,
                        ...(formData.assignedRegionId
                            ? { assignedRegionId: formData.assignedRegionId }
                            : {}),
                        ...(formData.purchaseDate
                            ? { purchaseDate: formData.purchaseDate.toString() }
                            : {}),
                        ...(formData.trainingDate
                            ? { trainingDate: formData.trainingDate.toString() }
                            : {}),
                    });
                    createdTerritoryIdRef.current = created.id;
                }

                for (const fs of feeStructures) {
                    if (persistedFeeStructureKeysRef.current.has(fs._key)) {
                        continue;
                    }
                    await createFeeStructureMutation.mutateAsync({
                        territoryId: createdTerritoryIdRef.current,
                        ...buildFeeStructureInput(fs),
                    });
                    persistedFeeStructureKeysRef.current.add(fs._key);
                }
            }

            onSuccess();
            onDismiss();
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : "Failed to save.";
            setPanelMessage("error", message);
        } finally {
            setIsSaving(false);
        }
    }, [
        formData,
        isEditing,
        editingTerritory,
        feeStructures,
        originalFeeStructureIds,
        createMutation,
        updateMutation,
        createFeeStructureMutation,
        updateFeeStructureMutation,
        deleteFeeStructureMutation,
        buildFeeStructureInput,
        validateFeeStructures,
        onSuccess,
        onDismiss,
        setPanelMessage,
    ]);

    const showDefaultsBanner =
        !isEditing && initialFeeStructures.some(fs => fs.fromDefault);

    return (
        <Panel
            isOpen={true}
            headerText={isEditing ? "Edit Territory" : "Add Territory"}
            onDismiss={onDismiss}
            type={PanelType.large}>
            <div className="space-y-6">
                <fieldset className="border-b-2 border-gray-200 pb-6">
                    <legend className="text-base font-semibold text-gray-900 mb-3">
                        Territory Details
                    </legend>
                    <div className="space-y-4">
                        <Input
                            label="Name"
                            value={formData.name}
                            onChange={e =>
                                setFormData(prev => ({
                                    ...prev,
                                    name: e.target.value,
                                }))
                            }
                            disabled={isSaving}
                        />
                        <Input
                            label="Territory Code"
                            value={formData.code}
                            onChange={e =>
                                setFormData(prev => ({
                                    ...prev,
                                    code: e.target.value,
                                }))
                            }
                            disabled={isSaving}
                        />
                        <Select
                            label="Status"
                            value={formData.status}
                            onChange={e =>
                                setFormData(prev => ({
                                    ...prev,
                                    status: e.target.value as FranchiseTerritoryStatus,
                                }))
                            }
                            disabled={isSaving}>
                            <option value={FranchiseTerritoryStatus.Active}>
                                Active
                            </option>
                            <option value={FranchiseTerritoryStatus.Inactive}>
                                Inactive
                            </option>
                        </Select>
                        <Select
                            label="Assigned Region (optional)"
                            value={formData.assignedRegionId}
                            onChange={e =>
                                setFormData(prev => ({
                                    ...prev,
                                    assignedRegionId: e.target.value as RegionId | "",
                                }))
                            }
                            disabled={isSaving}>
                            <option value="">No region</option>
                            {childRegions.map(region => (
                                <option key={region.id} value={region.id}>
                                    {region.name}
                                </option>
                            ))}
                        </Select>
                    </div>
                </fieldset>
                <fieldset className="border-b-2 border-gray-200 pb-6">
                    <legend className="text-base font-semibold text-gray-900 mb-3">
                        Zip Codes
                    </legend>
                    <ReorderableStringList
                        label=""
                        initial={formData.zipCodes}
                        onCommit={values =>
                            setFormData(prev => ({
                                ...prev,
                                zipCodes: values,
                            }))
                        }
                        addButtonLabel="Add zip code"
                    />
                </fieldset>
                <fieldset className="border-b-2 border-gray-200 pb-6">
                    <legend className="text-base font-semibold text-gray-900 mb-3">
                        Key Dates
                    </legend>
                    <div className="space-y-4">
                        <CalendarDateInput
                            label="Purchase Date"
                            value={formData.purchaseDate}
                            onChange={val =>
                                setFormData(prev => ({
                                    ...prev,
                                    purchaseDate: val,
                                }))
                            }
                        />
                        <CalendarDateInput
                            label="Training Date"
                            value={formData.trainingDate}
                            onChange={val =>
                                setFormData(prev => ({
                                    ...prev,
                                    trainingDate: val,
                                }))
                            }
                        />
                    </div>
                </fieldset>
                <fieldset>
                    <legend className="text-base font-semibold text-gray-900 mb-3">
                        Fee Structures
                    </legend>
                    {showDefaultsBanner ? (
                        <div className="mb-4">
                            <InlineBanner type="info">
                                <div className="flex items-center justify-between gap-3">
                                    <span>
                                        Pre-filled from your territory defaults — set each
                                        commencement date to schedule the fees.
                                    </span>
                                    <SecondaryCompactButton
                                        onClick={resetToDefaults}
                                        disabled={isSaving}>
                                        Reset to defaults
                                    </SecondaryCompactButton>
                                </div>
                            </InlineBanner>
                        </div>
                    ) : null}
                    <div className="space-y-4">
                        {feeStructures.map((fs, fsIndex) => (
                            <div
                                key={fs._key}
                                className="border border-gray-200 rounded-md p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                        {fs.name.trim() || `Fee Structure ${fsIndex + 1}`}
                                        {fs.fromDefault ? (
                                            <Badge value="From defaults" type="info" />
                                        ) : null}
                                    </span>
                                    <DeleteButton
                                        backgroundColor="bg-white"
                                        size="sm"
                                        confirm={{
                                            title: "Delete Fee Structure?",
                                            message: `Remove "${fs.name || "this fee structure"}" and all its schedule entries?`,
                                            confirmLabel: "Delete",
                                        }}
                                        onClick={() => removeFeeStructure(fsIndex)}
                                    />
                                </div>
                                {fs.schedule.kind === "relative" ? (
                                    <LockedFeeStructureCard
                                        feeStructure={fs}
                                        schedule={fs.schedule}
                                        isSaving={isSaving}
                                        onCommencementDateChange={val =>
                                            updateFeeStructureField(
                                                fsIndex,
                                                "commencementDate",
                                                val,
                                            )
                                        }
                                        onEditManually={() =>
                                            editScheduleManually(fsIndex)
                                        }
                                    />
                                ) : (
                                    <EditableFeeStructureCard
                                        feeStructure={fs}
                                        entries={fs.schedule.entries}
                                        fsIndex={fsIndex}
                                        isSaving={isSaving}
                                        updateFeeStructureField={updateFeeStructureField}
                                        onModeChange={newMode =>
                                            changeMode(fsIndex, newMode)
                                        }
                                        updateScheduleEntry={updateScheduleEntry}
                                        addScheduleEntry={addScheduleEntry}
                                        removeScheduleEntry={removeScheduleEntry}
                                    />
                                )}
                            </div>
                        ))}
                        <SecondaryButton onClick={addFeeStructure} disabled={isSaving}>
                            Add Fee Structure
                        </SecondaryButton>
                    </div>
                </fieldset>
                {validationError ? (
                    <InlineBanner type="error">{validationError}</InlineBanner>
                ) : null}
                {panelMessage ? (
                    <InlineBanner type={panelMessage.type}>
                        {panelMessage.content}
                    </InlineBanner>
                ) : null}
                <div className="flex space-x-2">
                    <PrimaryButton onClick={handleSubmit} disabled={isSaving}>
                        {isSaving ? "Saving..." : "Save"}
                    </PrimaryButton>
                    <SecondaryButton onClick={onDismiss} disabled={isSaving}>
                        Cancel
                    </SecondaryButton>
                </div>
            </div>
        </Panel>
    );
};

interface LockedFeeStructureCardProps {
    feeStructure: FeeStructureFormData;
    schedule: Extract<FeeStructureScheduleFormData, { kind: "relative" }>;
    isSaving: boolean;
    onCommencementDateChange: (val: CalendarDate | null) => void;
    onEditManually: () => void;
}

const LockedFeeStructureCard: React.FC<LockedFeeStructureCardProps> = props => {
    const { feeStructure, schedule, isSaving, onCommencementDateChange, onEditManually } =
        props;
    const steps = sortedSteps(schedule.steps);
    return (
        <>
            <p className="text-sm text-gray-500">
                {feeStructureModeLabel(feeStructure.mode)}
                {feeStructure.mode === FranchiseFeeStructureMode.PercentPlusMinimum
                    ? ` · ${feeStructure.feePercentDisplay}% monthly fee`
                    : ""}
            </p>
            <CalendarDateInput
                label="Commencement Date"
                textSize="sm"
                value={feeStructure.commencementDate}
                onChange={onCommencementDateChange}
            />
            <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                    {feeScheduleLabel(feeStructure.mode)}
                </p>
                <table className="w-full text-sm">
                    <thead>
                        <tr>
                            <th className="text-left pb-1 pr-2 font-medium text-gray-600">
                                When
                            </th>
                            <th className="text-left pb-1 pr-2 font-medium text-gray-600">
                                {feeAmountColumnLabel(feeStructure.mode)}
                            </th>
                            <th className="text-left pb-1 font-medium text-gray-600">
                                Effective Date
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {steps.map(step => {
                            const effective = computeEffectiveDate(
                                feeStructure.commencementDate,
                                step.monthsAfterCommencement,
                            );
                            return (
                                <tr key={step._key}>
                                    <td className="pr-2 py-1 text-gray-800">
                                        {stepWhenLabel(step.monthsAfterCommencement)}
                                    </td>
                                    <td className="pr-2 py-1 text-gray-800">
                                        {formatDollars(parseCents(step.amountDollars))}
                                    </td>
                                    <td className="py-1">
                                        {effective ? (
                                            <span className="text-gray-800">
                                                {formatDateNoTime(effective.toString())}
                                            </span>
                                        ) : (
                                            <span className="text-gray-400">TBD</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {!feeStructure.commencementDate ? (
                    <p className="text-xs text-gray-500 mt-1">
                        Set the commencement date to schedule these fees, or edit the
                        schedule manually.
                    </p>
                ) : null}
                <div className="mt-2">
                    <SecondaryCompactButton onClick={onEditManually} disabled={isSaving}>
                        Edit schedule manually
                    </SecondaryCompactButton>
                </div>
            </div>
        </>
    );
};

interface EditableFeeStructureCardProps {
    feeStructure: FeeStructureFormData;
    entries: ScheduleEntryFormData[];
    fsIndex: number;
    isSaving: boolean;
    updateFeeStructureField: <K extends keyof FeeStructureFormData>(
        fsIndex: number,
        field: K,
        value: FeeStructureFormData[K],
    ) => void;
    onModeChange: (newMode: FranchiseFeeStructureMode) => void;
    updateScheduleEntry: (
        fsIndex: number,
        entryIndex: number,
        field: keyof ScheduleEntryFormData,
        value: string,
    ) => void;
    addScheduleEntry: (fsIndex: number) => void;
    removeScheduleEntry: (fsIndex: number, entryIndex: number) => void;
}

const EditableFeeStructureCard: React.FC<EditableFeeStructureCardProps> = props => {
    const {
        feeStructure: fs,
        entries,
        fsIndex,
        isSaving,
        updateFeeStructureField,
        onModeChange,
        updateScheduleEntry,
        addScheduleEntry,
        removeScheduleEntry,
    } = props;
    return (
        <>
            <Input
                label="Name"
                textSize="sm"
                value={fs.name}
                onChange={e => updateFeeStructureField(fsIndex, "name", e.target.value)}
                disabled={isSaving}
            />
            <Select
                label="Mode"
                textSize="sm"
                value={fs.mode}
                onChange={e => onModeChange(e.target.value as FranchiseFeeStructureMode)}
                disabled={isSaving}>
                <option value={FranchiseFeeStructureMode.FlatFee}>
                    {feeStructureModeLabel(FranchiseFeeStructureMode.FlatFee)}
                </option>
                <option value={FranchiseFeeStructureMode.PercentPlusMinimum}>
                    {feeStructureModeLabel(FranchiseFeeStructureMode.PercentPlusMinimum)}
                </option>
            </Select>
            {fs.mode === FranchiseFeeStructureMode.PercentPlusMinimum ? (
                <Input
                    label="Monthly fee percent (%)"
                    textSize="sm"
                    type="number"
                    step="0.01"
                    min="0"
                    value={fs.feePercentDisplay}
                    onChange={e =>
                        updateFeeStructureField(
                            fsIndex,
                            "feePercentDisplay",
                            e.target.value,
                        )
                    }
                    disabled={isSaving}
                />
            ) : null}
            <CalendarDateInput
                label="Commencement Date"
                textSize="sm"
                value={fs.commencementDate}
                onChange={val =>
                    updateFeeStructureField(fsIndex, "commencementDate", val)
                }
            />
            <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                    {feeScheduleLabel(fs.mode)}
                </p>
                <table className="w-full text-sm">
                    <thead>
                        <tr>
                            <th className="text-left pb-1 pr-2 font-medium text-gray-600">
                                Effective Date
                            </th>
                            <th className="text-left pb-1 pr-2 font-medium text-gray-600">
                                {feeAmountColumnLabel(fs.mode)}
                            </th>
                            <th className="w-8"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map((entry, eIdx) => (
                            <tr key={entry._key}>
                                <td className="pr-2 pb-1">
                                    <DatePicker
                                        label={null}
                                        value={entry.effectiveDate || null}
                                        onChange={v =>
                                            updateScheduleEntry(
                                                fsIndex,
                                                eIdx,
                                                "effectiveDate",
                                                v ?? "",
                                            )
                                        }
                                        disabled={isSaving}
                                        required={false}
                                        textSize="sm"
                                        onOpenChange={() => undefined}
                                        minValue={null}
                                        maxValue={null}
                                        visibleYears={20}
                                    />
                                </td>
                                <td className="pr-2 pb-1">
                                    <Input
                                        type="number"
                                        textSize="sm"
                                        step="0.01"
                                        min="0"
                                        value={entry.amountDollars}
                                        onChange={e =>
                                            updateScheduleEntry(
                                                fsIndex,
                                                eIdx,
                                                "amountDollars",
                                                e.target.value,
                                            )
                                        }
                                        disabled={isSaving}
                                    />
                                </td>
                                <td className="pb-1">
                                    <DeleteButton
                                        backgroundColor="bg-white"
                                        size="sm"
                                        onClick={() => removeScheduleEntry(fsIndex, eIdx)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <SecondaryCompactButton
                    onClick={() => addScheduleEntry(fsIndex)}
                    disabled={isSaving}>
                    Add Entry
                </SecondaryCompactButton>
            </div>
        </>
    );
};
